import React, { useState } from 'react';
import { Mic, X, Loader2, ChevronLeft, Music } from 'lucide-react';
import { useSermon } from '../hooks/useSermon';
import { SpeechControls } from './SpeechControls';
import { YouTubePlayer } from './YouTubePlayer';

interface SermonButtonProps {
  devotionId: number;
}

export function SermonButton({ devotionId }: SermonButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showAudio, setShowAudio] = useState(false);
  const { sermon, isLoading, error } = useSermon(devotionId);

  if (isLoading) {
    return (
      <button 
        disabled
        className="flex items-center space-x-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 rounded-lg cursor-not-allowed"
      >
        <Loader2 size={16} className="animate-spin" />
        <span>Loading...</span>
      </button>
    );
  }

  if (error || !sermon) {
    return null;
  }

  // Split sermon content into paragraphs
  const paragraphs = sermon.content.split('\n').filter(p => p.trim());

  // Define a set of colors to alternate between
  const colors = [
    'bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300',
    'bg-purple-50 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300',
    'bg-emerald-50 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300',
    'bg-amber-50 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300',
    'bg-rose-50 dark:bg-rose-900/50 text-rose-700 dark:text-rose-300',
    'bg-cyan-50 dark:bg-cyan-900/50 text-cyan-700 dark:text-cyan-300',
    'bg-indigo-50 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300',
    'bg-teal-50 dark:bg-teal-900/50 text-teal-700 dark:text-teal-300'
  ];

  // Construct audio URL
  const audioUrl = sermon.song_url || null;

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center space-x-2 px-3 py-1.5 bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-800 transition-colors"
      >
        <Mic size={16} />
        <span>Sermon</span>
      </button>

      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-4xl w-full relative max-h-[90vh] overflow-y-auto">
            <div className="flex flex-col space-y-4 mb-6">
              <button
                onClick={() => setIsOpen(false)}
                className="flex items-center space-x-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <ChevronLeft size={20} />
                <span>Back to Devotion</span>
              </button>
              
              <div className="flex flex-wrap gap-2">
              {sermon.song_url && (
               <>
               <button
                 onClick={() => {console.log('Audio URL:', audioUrl); setShowAudio(!showAudio)}}
                 className="flex items-center space-x-2 px-3 py-1.5 bg-rose-100 dark:bg-rose-900 text-rose-600 dark:text-rose-400 rounded-lg hover:bg-rose-200 dark:hover:bg-rose-800 transition-colors"
               >
                 <Music size={16} />
                 <span>{showAudio ? 'Hide Song' : 'Play Song'}</span>
               </button>
               {showAudio && (
                 <audio
                  controls
                  autoPlay
                  className="w-full mt-2"
                  onError={() => alert('Failed to load audio. Please try again.')}
                 >
                  <source
                    src={audioUrl!}
                    type="audio/mpeg"
                  />
                    Your browser does not support audio playback.
                </audio>
              )}
             </>
             )}
                <SpeechControls 
                  text={sermon.content} 
                  label="Listen to Sermon"
                />
              </div>
            </div>

            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                {sermon.title}
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                By {sermon.author}
              </p>
            </div>

            <div className="space-y-6">
              {paragraphs.map((paragraph, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg ${colors[index % colors.length]} leading-relaxed`}
                >
                  {paragraph}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}


    </>
  );
}